import {
  type CartesianPose,
  ZERO_POSE,
  additionBetweenPoses,
  invertPose,
  tooltipCoordinatesToBaseCoordinates,
} from '@sb/geometry';

/** Lift a stored tool frame into the base-aligned axes it composes in. */
export function recordTcpOffsetFromToolFrame(
  toolFrame: { tcpOffset?: CartesianPose } | null | undefined,
): CartesianPose | undefined {
  return toolFrame?.tcpOffset
    ? tooltipCoordinatesToBaseCoordinates(toolFrame.tcpOffset)
    : undefined;
}

/**
 * Re-expresses a recorded demonstration for the tooltip mounted now.
 *
 * Recorded frames are flange poses, so the recording's tooltip traced
 * `frame ∘ recordTcpOffset`. Putting today's tooltip on that path needs the
 * flange at `frame ∘ recordTcpOffset ∘ inverse(liveTcpOffset)`, which is this
 * transform post-multiplied onto every frame.
 *
 * `projected` is false when it cannot move anything, so callers can skip that
 * composition over a whole recording.
 */
export function resolveDemonstrationFrameTransform({
  recordTcpOffset,
  liveTcpOffset,
}: {
  recordTcpOffset: CartesianPose | undefined;
  liveTcpOffset: CartesianPose | undefined;
}): { frameTransform: CartesianPose; projected: boolean } {
  if (!recordTcpOffset || !liveTcpOffset) {
    return { frameTransform: { ...ZERO_POSE }, projected: false };
  }

  return {
    frameTransform: additionBetweenPoses(
      recordTcpOffset,
      invertPose(liveTcpOffset),
    ),
    projected: true,
  };
}

/**
 * The same re-expression, gated on `setActiveTooltip`.
 *
 * Flag off, a recording replays in the frame it was captured in. The robot
 * exposes no Active Tooltip to have been recorded against and no control to
 * change it, so re-expressing the path would move it by a tool offset the
 * operator can neither see nor choose.
 *
 * The flag is an argument rather than a read, so both states are reachable in
 * a test without mocking the flag module.
 */
export function resolveGatedDemonstrationFrameTransform({
  activeTooltipEnabled,
  recordTcpOffset,
  liveTcpOffset,
}: {
  activeTooltipEnabled: boolean;
  recordTcpOffset: CartesianPose | undefined;
  liveTcpOffset: CartesianPose | undefined;
}): { frameTransform: CartesianPose; projected: boolean } {
  if (!activeTooltipEnabled) {
    return { frameTransform: { ...ZERO_POSE }, projected: false };
  }

  return resolveDemonstrationFrameTransform({ recordTcpOffset, liveTcpOffset });
}
